import React, { useEffect, useMemo, useState } from "react";
import {
  Heart, Users, Wine, Utensils, Camera, Music, Coffee, Sun, Bus, MapPin, Clock, CheckCircle2,
  Home, MessageCircleMore, Radio
} from "lucide-react";
import { api } from "@/lib/api";
import { useClock } from "@/lib/clock";
import { useI18n } from "@/lib/i18n";
import { tContent } from "@/lib/content-i18n";

export const TL_ICONS = {
  heart: Heart, users: Users, wine: Wine, utensils: Utensils, camera: Camera,
  music: Music, coffee: Coffee, sun: Sun, bus: Bus, "map-pin": MapPin, clock: Clock,
  home: Home, "yes-quiero": MessageCircleMore,
};

export function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Europe/Madrid" });
}

export function computeStatus(events, now = new Date()) {
  const sorted = [...events].sort((a, b) => new Date(a.time_iso) - new Date(b.time_iso));
  return sorted.map((e, i) => {
    const t = new Date(e.time_iso);
    const next = sorted[i + 1] ? new Date(sorted[i + 1].time_iso) : null;
    let state = "upcoming";
    if (now >= t && (next === null || now < next)) state = "active";
    else if (now >= t) state = "done";
    return { ...e, state };
  });
}

export function LiveBadge({ events, t, kind = "program", now = new Date() }) {
  const { lang } = useI18n();
  const items = computeStatus(events, now);
  const active = items.find((e) => e.state === "active");
  const upcoming = items.find((e) => e.state === "upcoming");
  const allDone = items.length > 0 && items.every((e) => e.state === "done");
  const noneStarted = items.length > 0 && items.every((e) => e.state === "upcoming");

  const trTitle = (s) => tContent(s, lang, "program");

  let message = null;
  if (noneStarted) {
    if (kind === "bus") message = t.bus.notDeparted;
    else message = `${t.programa.nextLabel} ${trTitle(upcoming.title)} (${formatTime(upcoming.time_iso)})`;
  } else if (allDone) {
    message = kind === "bus" ? t.bus.finished : t.programa.finished;
  } else if (active) {
    message = upcoming
      ? `${kind === "bus" ? t.bus.happeningNow : t.programa.happeningNow} ${trTitle(active.title)} · ${kind === "bus" ? t.bus.nextLabel : t.programa.nextLabel} ${trTitle(upcoming.title)} (${formatTime(upcoming.time_iso)})`
      : `${kind === "bus" ? t.bus.happeningNow : t.programa.happeningNow} ${trTitle(active.title)}`;
  }

  if (!message) return null;

  return (
    <div className="s-card flex items-center gap-3 px-4 py-3 mb-5 max-w-2xl mx-auto" data-testid="live-badge">
      <span className="relative inline-flex shrink-0">
        <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-[color:var(--ink-wine)] opacity-50 animate-ping" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[color:var(--ink-wine)]" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[0.62rem] tracking-[0.28em] uppercase font-bold text-[color:var(--ink-wine)]">
          {kind === "bus" ? t.bus.liveStatus : t.programa.liveStatus}
        </p>
        <p className="text-sm text-[color:var(--ink-deep)] mt-0.5 leading-snug">{message}</p>
      </div>
    </div>
  );
}

export function TimelineList({ items, t, prefix = "tl" }) {
  const { lang } = useI18n();
  return (
    <ol className="relative max-w-2xl mx-auto pl-10" data-testid="timeline-list">
      {/* Continuous vertical line through the dots */}
      <div className="absolute left-[15px] top-3 bottom-3 w-px bg-[color:var(--olive)]/35" />
      {items.map((e, idx) => {
        const Icon = TL_ICONS[e.icon] || Clock;
        const isActive = e.state === "active";
        const isDone = e.state === "done";
        return (
          <li key={e.id || idx} className={`relative pb-5 ${idx === items.length - 1 ? "pb-0" : ""}`} data-testid={`${prefix}-${idx}`}>
            <div
              className={`absolute -left-[28px] top-3 w-[26px] h-[26px] rounded-full flex items-center justify-center transition-all bg-[color:var(--bg-paper)] ${
                isActive
                  ? "ring-4 ring-[color:var(--ink-wine)] scale-125 shadow-lg shadow-[color:var(--ink-wine)]/30"
                  : isDone
                  ? "ring-1 ring-[color:var(--olive)]"
                  : "ring-1 ring-[color:var(--olive)]/40"
              }`}
              data-testid={`${prefix}-dot-${idx}`}
            >
              {isActive ? (
                <span className="relative inline-flex">
                  <span className="absolute inline-flex h-3 w-3 rounded-full bg-[color:var(--ink-wine)] opacity-60 animate-ping" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-[color:var(--ink-wine)]" />
                </span>
              ) : isDone ? (
                <CheckCircle2 size={14} strokeWidth={2.4} className="text-[color:var(--olive)]" />
              ) : (
                <Icon size={11} strokeWidth={2} className="text-[color:var(--olive)]/70" />
              )}
            </div>
            <div
              className={`s-card px-4 py-3 sm:px-5 sm:py-4 transition-all ${
                isActive
                  ? "ring-2 ring-[color:var(--ink-wine)] bg-[color:var(--bg-blush)] shadow-lg scale-[1.02] -translate-x-1"
                  : isDone
                  ? "opacity-65"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className={`font-serif text-base font-medium ${isActive ? "text-[color:var(--ink-wine)] font-bold" : "text-[color:var(--ink-wine)]"}`}>
                  {formatTime(e.time_iso)}
                </span>
                {isActive && (
                  <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bold px-2.5 py-1 rounded-full bg-[color:var(--ink-wine)] text-white animate-pulse shadow-md">
                    ● {t.programa.nowActive}
                  </span>
                )}
              </div>
              <h3 className={`font-sans text-sm sm:text-base mb-0.5 ${isActive ? "font-bold text-[color:var(--ink-wine)]" : "font-semibold text-[color:var(--ink-deep)]"}`}>
                {tContent(e.title, lang, "program")}
              </h3>
              {e.description && (
                <p className={`text-xs sm:text-sm leading-snug ${isActive ? "text-[color:var(--ink-deep)]" : "text-[color:var(--ink-soft)]"}`}>
                  {tContent(e.description, lang, "program")}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export default function Timeline({ source, t, eyebrow, title, subtitle, testId }) {
  const [events, setEvents] = useState([]);
  const { now } = useClock();

  useEffect(() => {
    const load = async () => {
      try { const { data } = await api.get(`/${source}`); setEvents(data || []); } catch {}
    };
    load();
  }, [source]);

  const items = useMemo(() => computeStatus(events, now), [events, now]);

  return (
    <main className="section" data-testid={testId}>
      <p className="eyebrow text-center mb-1">{eyebrow}</p>
      <h1 className="page-title font-script">{title}</h1>
      {subtitle && <p className="text-center text-[color:var(--ink-soft)] italic -mt-4 mb-6">{subtitle}</p>}

      <LiveBadge events={events} t={t} kind="program" now={now} />

      {items.length === 0 ? (
        <div className="s-card s-card-pad text-center max-w-xl mx-auto">
          <p className="text-[color:var(--ink-soft)]">—</p>
        </div>
      ) : (
        <TimelineList items={items} t={t} prefix="tl" />
      )}
    </main>
  );
}
